// WHAT: Custom hook for Oura smart synchronization with recent and full sync modes.
// WHY:  Provides state management and functions for syncing Oura health data with backend, supporting both recent (incremental) and full (complete) sync modes.
// DOMAIN: Health → Oura
// DEPENDENCIES: React, @tauri-apps/api
// RISKS:
//   - WARN: Tauri command 'sync_oura_smart' must exist in backend; failures will throw errors.
//   - NOTE: Error handling is minimal; detailed error information may be lost.
//   - WARN: Full sync mode may consume significant resources and time; consider user feedback.

import { core } from "@tauri-apps/api";
import { useState, useCallback } from "react";

/**
 * WHAT:
 *   Custom hook for Oura smart synchronization with recent and full sync modes.
 *
 * WHY:
 *   Provides centralized state management and functions for syncing Oura health data
 *   with backend. Supports two sync modes: recent (incremental, faster) and full
 *   (complete, slower but comprehensive).
 *
 * @returns Object containing syncRecent, syncFull functions, running state, result, and error
 *
 * SIDE EFFECTS:
 *   - Updates running state during sync operations
 *   - Updates result state on successful sync
 *   - Updates error state on sync failures
 *   - Invokes Tauri command 'sync_oura_smart' with mode parameter
 *
 * RISKS:
 *   - WARN: Tauri command 'sync_oura_smart' must exist in backend; failures will throw errors.
 *   - NOTE: Error handling is minimal; detailed error information may be lost.
 *   - WARN: Full sync mode may consume significant resources and time; consider user feedback.
 *   - NOTE: Result is a string; consider structured response type for better error handling.
 *
 */
export function useOuraSmartSync() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<null | string>(null);
  const [error, setError] = useState<Error | null>(null);

  /**
   * WHAT:
   *   Syncs recent Oura data (incremental sync).
   *
   * WHY:
   *   Provides fast synchronization of only recent data changes, reducing API calls
   *   and improving performance for regular syncs.
   *
   * SIDE EFFECTS:
   *   - Sets running to true at start
   *   - Clears error and result states
   *   - Invokes Tauri command 'sync_oura_smart' with mode 'recent'
   *   - Updates result state on success
   *   - Updates error state on failure
   *   - Sets running to false on completion
   *
   * RISKS:
   *   - NOTE: Recent mode behavior depends on backend implementation; may not sync all expected data.
   *   - WARN: Tauri command must exist; failures will throw errors.
   */
  const syncRecent = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const res = await core.invoke<string>(
        "sync_oura_smart",
        { mode: "recent" }, // NOTE: oder ohne mode parameter (default ist recent)
      );
      setResult(res);
    } catch (error_) {
      setError(error_ as Error);
    } finally {
      setRunning(false);
    }
  }, []);

  /**
   * WHAT:
   *   Syncs full Oura data (complete sync).
   *
   * WHY:
   *   Provides comprehensive synchronization of all available Oura data, useful
   *   for initial setup or recovering from sync issues.
   *
   * SIDE EFFECTS:
   *   - Sets running to true at start
   *   - Clears error and result states
   *   - Invokes Tauri command 'sync_oura_smart' with mode 'full'
   *   - Updates result state on success
   *   - Updates error state on failure
   *   - Sets running to false on completion
   *
   * RISKS:
   *   - WARN: Full sync may consume significant resources and time; consider user feedback.
   *   - WARN: Tauri command must exist; failures will throw errors.
   *   - NOTE: Full sync may cause rate limiting with Oura API.
   */
  const syncFull = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const res = await core.invoke<string>("sync_oura_smart", {
        mode: "full",
      });
      setResult(res);
    } catch (error_) {
      setError(error_ as Error);
    } finally {
      setRunning(false);
    }
  }, []);

  return {
    error,
    result,
    running,
    syncFull,
    syncRecent,
  };
}
